import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Signal,
  viewChild
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import {
  DialogBase,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeInputContainerModule,
  TiimeSnackbarService
} from 'tiime-material';

import { ExternalLinkApiService } from '@api-services/external-link-api.service';
import { ExternalLinkForm } from '@forms/external-link-form';
import { ExternalLink } from '@models/external-link';

import { CreateOrUpdateExternalLinkDialogData } from './create-or-update-external-link-dialog-data';

@Component({
  selector: 'app-create-or-update-external-link-dialog',
  standalone: true,
  imports: [AsyncPipe, ReactiveFormsModule, TiimeDialogModule, TiimeButtonModule, TiimeInputContainerModule],
  templateUrl: './create-or-update-external-link-dialog.component.html',
  styleUrls: ['./create-or-update-external-link-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrUpdateExternalLinkDialogComponent
  extends DialogBase<CreateOrUpdateExternalLinkDialogComponent>
  implements AfterViewInit
{
  readonly nameInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild.required<ElementRef<HTMLInputElement>>('nameInput');

  readonly externalLinkForm: ExternalLinkForm = new ExternalLinkForm();

  constructor(
    readonly dialogRef: MatDialogRef<CreateOrUpdateExternalLinkDialogComponent>,
    private readonly externalLinkApiService: ExternalLinkApiService,
    private readonly snackbar: TiimeSnackbarService,
    @Inject(MAT_DIALOG_DATA) data: CreateOrUpdateExternalLinkDialogData
  ) {
    super(dialogRef);

    this.initExternalLinkForm(data.externalLink);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.nameInput().nativeElement.select());
  }

  @MarkFormAsTouchedIfInvalid('externalLinkForm')
  submitExternalLinkForm(): void {
    const externalLink = this.externalLinkForm.toExternalLink();
    this.externalLinkForm.disable();
    this.isLoading.set(true);

    const obs = externalLink.id ? this.updateExternalLink(externalLink) : this.createExternalLink(externalLink);

    obs
      .pipe(
        tap((createdOrUpdatedExternalLink: ExternalLink) => this.dialogRef.close(createdOrUpdatedExternalLink)),
        finalize(() => {
          this.externalLinkForm.enable();
          this.isLoading.set(false);
        })
      )
      .subscribe();
  }

  private initExternalLinkForm(externalLink: ExternalLink): void {
    this.externalLinkForm.fromExternalLink(externalLink);
  }

  private createExternalLink(externalLink: ExternalLink): Observable<ExternalLink> {
    return this.externalLinkApiService
      .createExternalLink(externalLink)
      .pipe(tap(() => this.snackbar.openSuccess('Le lien a bien été ajouté')));
  }

  private updateExternalLink(externalLink: ExternalLink): Observable<ExternalLink> {
    return this.externalLinkApiService
      .updateExternalLink(externalLink)
      .pipe(tap(() => this.snackbar.openSuccess('Le lien a bien été modifié')));
  }
}
